import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { useField } from 'formik'
import { Button } from 'src/components/shared/Button'
import {
  CONTRACTOR_TYPE_OPTIONS,
  PAYMENT_PERIOD_OPTIONS,
  PAYOUT_DESTINATION_OPTIONS,
} from 'src/constants/payment/constants'
import { VendorFeeMultiSelectField } from './VendorFeeMultiSelectField'
import { VendorFeeSelectField } from './VendorFeeSelectField'
import { VendorFeeTextField } from './VendorFeeTextField'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  index: number
  isReadOnly: boolean
  onRemove(index: number): void
}

/**
 * One vendor fee as a bordered block: a heading (the fee's title, or its ordinal until one is
 * typed) with Remove at the right, then the fields as a labelled grid. Field order is still the
 * live Retool tab's — the three amounts, then type and title, then the two audiences — so
 * nobody moving between the two tabs has to re-find a field; only the shape changed, from a
 * seven-column table that needed a horizontal scroll to something that fits the page.
 */
const VendorFeeRowComponent = ({ index, isReadOnly, onRemove }: Props) => {
  const [titleField] = useField<string>(`rows[${index}].title`)

  const heading = titleField.value.trim() || `Vendor fee ${index + 1}`

  const handleRemove = useCallback(() => {
    onRemove(index)
  }, [index, onRemove])

  return (
    <div
      data-testid={`vendor-fee-row-${index}`}
      css={tw`flex flex-col gap-4 rounded-lg border border-solid border-shades-100 p-4`}
    >
      <div css={tw`flex flex-row items-center justify-between gap-4`}>
        <Typography variant="h5" css={tw`min-w-0 truncate text-shades-1000`}>
          {heading}
        </Typography>
        {isReadOnly ? null : (
          <Tooltip title="Remove">
            <span>
              <Button
                color="grey"
                size="tiny"
                iconOnly
                aria-label="Remove vendor fee"
                leftIcon={<GSIcon iconName="gs_trash_can" size={20} css={tw`text-shades-1000`} />}
                onClick={handleRemove}
                data-testid={`remove-vendor-fee-${index}`}
              />
            </span>
          </Tooltip>
        )}
      </div>
      <div css={tw`grid grid-cols-1 gap-4 md:grid-cols-3`}>
        <VendorFeeTextField
          name={`rows[${index}].fee`}
          label="Fee (cents)"
          placeholder="0"
          disabled={isReadOnly}
        />
        <VendorFeeTextField
          name={`rows[${index}].rate`}
          label="Rate (%)"
          placeholder="0.00"
          disabled={isReadOnly}
        />
        <VendorFeeTextField
          name={`rows[${index}].feeCapCents`}
          label="Max Fee (cents)"
          placeholder="0"
          disabled={isReadOnly}
        />
      </div>
      <div css={tw`grid grid-cols-1 gap-4 md:grid-cols-2`}>
        <VendorFeeSelectField
          name={`rows[${index}].type`}
          label="Type"
          options={PAYMENT_PERIOD_OPTIONS}
          placeholder="Select a type"
          disabled={isReadOnly}
        />
        <VendorFeeTextField
          name={`rows[${index}].title`}
          label="Title"
          placeholder="Enter a title"
          disabled={isReadOnly}
        />
        <VendorFeeMultiSelectField
          name={`rows[${index}].applicableDestinations`}
          label="Destination Type"
          options={PAYOUT_DESTINATION_OPTIONS}
          placeholder="Select options"
          disabled={isReadOnly}
        />
        <VendorFeeMultiSelectField
          name={`rows[${index}].contractorType`}
          label="Contractor Type"
          options={CONTRACTOR_TYPE_OPTIONS}
          placeholder="Select options"
          disabled={isReadOnly}
        />
      </div>
    </div>
  )
}

export const VendorFeeRow = memo(VendorFeeRowComponent)
